import { Injectable, Predicate } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CustomCategorySearchToolName,
  SelectedSuggestionPillDataModel as FilterConfig,
  SuggestionPillType,
} from '@omni/models/search-config-data-model';
import { SearchConfigService } from '../search/search-config.service';
import { CustomerAssessService } from '../customer-assess/customer-assess.service';
import { AuthenticationService } from '../authentication.service';
import { DiskService } from '../disk/disk.service';
import { DeviceService } from '../device/device.service';
import { AccountOfflineService } from '../account/account.offline.service';
import { UserTagService } from '../user-tag/user-tag.service';
import Fuse from 'fuse.js';
import {
  AssessmentAttributeType,
  EntityOptions,
} from '@omni/classes/customer-assessment/assessment-template.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { ACCOUNT_ACCOUNT_AFFILIATIONS_REF_ENTITY } from '@omni/config/dynamic-forms/affiliations-contants';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { ControlDataType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { getSearchSuggestionsData } from '@omni/utility/global-search.utility';
import { LocalizationService } from '@omni/services/localization/localization.service';
import { AccountDataService } from '@omni/data-services/accounts/account.data.service';
import { UIService } from '../ui/ui.service';
import { Account, AccountGlobalSearchDTO } from '@omni/classes/account/account.class';
import { OpportunityManagementService } from '../opportunity-management/opportunity-management.service';
import { SearchConfigDataService } from '@omni/data-services/search-config/search-config-data-service';
import { ContactDataService } from '@omni/data-services/contact/contact.data.service';
import { isEmpty, pick } from 'lodash-es';
import type { Collection } from 'lodash';
import { _ } from '@omni/utility/lodash-chain';

/**
 * Constants for better maintainability and configuration
 */
// Fields that are searchable in account objects for entity-level character search
const ACCOUNT_SEARCHABLE_FIELDS = new Set([
  "accountName",
  "city",
  "postal",
  "accountType",
  "positionname",
  "extendedDetails"
]);

// Categories that are excluded from grouped filter processing
const EXCLUDED_CATEGORIES = ['listPeriodTag', 'tag', 'Tag', 'Opportunities'];

// Categories that represent user tags
const TAG_CATEGORIES = ['Tag', 'tag'];

// Fuse.js search thresholds for different search types
const FUSE_SEARCH_THRESHOLD = 0.1; // Default threshold for most searches
const PHONE_SEARCH_THRESHOLD = 0.05; // Lower threshold for phone number searches

/**
 * Generic Fuse.js search utility function for fuzzy searching
 * @param items - Array of items to search through
 * @param filter - Filter configuration containing search parameters
 * @param threshold - Search threshold (lower = more strict)
 * @param keys - Fields to search in
 * @param searchText - Text to search for
 * @returns Lodash chain with filtered results
 */
function fuseSearch<T>(
  items: T[],
  filter: FilterConfig,
  threshold = FUSE_SEARCH_THRESHOLD,
  keys = [filter.categoryPath],
  searchText = filter.selectedFacet,
) {
  if (!items?.length || !filter) return _([]);

  const base = {
    keys,
    threshold: 0,
    distance: 100,
    includeScore: true,
  };

  // Adjust search parameters for character search and boolean fields
  if (
    filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH ||
    filter.type === SuggestionPillType.BOOLEAN_FIELD
  ) {
    base.threshold = threshold;
    base.distance = 1000;
    searchText = filter.charSearchText;
  }

  return _(new Fuse(items, base).search(searchText ?? ''))
    .filter(
      (r: any) => r.score < FUSE_SEARCH_THRESHOLD ||
        filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH,
    )
    .map((r: any) => r.item);
}

/**
 * Extracts extended details from account objects for search indexing
 * Handles coverage positions and account type information
 * @param extendedDetails - Extended details object from account
 * @param objString - Array to push extracted strings to
 */
function extractExtendedDetails(extendedDetails: any, objString: string[]): void {
  if (!extendedDetails || typeof extendedDetails !== 'object') return;

  for (const subKey in extendedDetails) {
    const value = extendedDetails[subKey];

    // Extract position names from coverage positions
    if (subKey === "coveragePositions" && Array.isArray(value)) {
      value.forEach(pos => {
        if (pos?.positionname) {
          objString.push(pos.positionname);
        }
      });
    } 
    // Extract formatted account type value
    else if (subKey === "indskr_accounttype") {
      const formattedValue = extendedDetails["indskr_accounttype@OData.Community.Display.V1.FormattedValue"];
      if (formattedValue) {
        objString.push(formattedValue);
      }
    }
  }
}

/**
 * Generates the appropriate field key for raw account data based on control data type
 * Handles different field types (picklist, lookup, boolean, etc.) with proper suffixes
 * @param filter - Filter configuration
 * @param searchConfig - Search configuration containing control data type info
 * @returns Formatted field key for raw data access
 */
function getAccountRawFieldKey(
  filter: FilterConfig,
  searchConfig: any,
): string {
  let suffix = '';
  let prefix = '';

  const type = searchConfig.controlDataType;

  // Handle formatted value fields (picklist, boolean, status, etc.)
  if (
    [
      ControlDataType.PicklistType,
      ControlDataType.BooleanType,
      ControlDataType.MultiSelectPicklistType,
      ControlDataType.StatusType,
      ControlDataType.StateType,
    ].includes(type as ControlDataType)
  ) {
    suffix = '@OData.Community.Display.V1.FormattedValue';
  } 
  // Handle lookup fields with multilingual support
  else if (type === ControlDataType.LookupType) {
    if (
      searchConfig.isMultilingualLookup &&
      filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
    ) {
      suffix = '_value';
      prefix = '_';
    } else {
      suffix = '_value@OData.Community.Display.V1.FormattedValue';
      prefix = '_';
    }
  }

  return prefix + searchConfig.categoryRelativePath + suffix;
}

/**
 * Extracts the appropriate search text from filter configuration
 * Handles multilingual lookup mapping for proper value resolution
 * @param filter - Filter configuration
 * @param searchConfig - Search configuration containing mapping values
 * @returns Resolved search text for filtering
 */
function getAccountRawSearchText(
  filter: FilterConfig,
  searchConfig: any,
): string {
  // Handle multilingual lookup mapping
  if (
    searchConfig.isMultilingualLookup &&
    filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
  ) {
    return (
      searchConfig.mappingValues.find(
        (m) => m.formattedValue === filter.selectedFacet,
      )?.actualValue ?? filter.selectedFacet
    );
  }

  return filter.selectedFacet || filter.charSearchText || '';
}

function getLinkedEntitySuffix(
  searchConfig: any,
  filter: FilterConfig,
): string {
  if (
    searchConfig.isMultilingualLookup &&
    filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
  ) {
    return '';
  }
  return '@OData.Community.Display.V1.FormattedValue';
}

function getLinkedEntitySearchText(
  filter: FilterConfig,
  searchConfig: any,
): string {
  if (filter.type === SuggestionPillType.CATEGORY_VALUESEARCH && searchConfig.isMultilingualLookup) {
    const mappedValue = searchConfig.mappingValues?.find(
      (m) => m.formattedValue === filter.selectedFacet,
    )?.actualValue;
    if (mappedValue) {
      return mappedValue;
    }
  }
  if (filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH) {
    return filter.charSearchText;
  }
  return filter.selectedFacet;
}

/**
 * Filters linked entity data with complex multilingual support
 * 
 * COMPLEX MULTILINGUAL LOGIC:
 * This function handles the most sophisticated filtering scenario - multilingual data.
 * It supports multiple data sources and translation mechanisms:
 * 
 * 1. DIRECT FIELD MATCHING:
 *    - Checks both suffixed and non-suffixed field names
 *    - Handles formatted values (e.g., '@OData.Community.Display.V1.FormattedValue')
 * 
 * 2. MULTILINGUAL LABEL TRANSLATION:
 *    - Uses localizationService.getTranslatedString() for mobile app form labels
 *    - Converts translated strings to searchable format
 * 
 * 3. ONEKEY MULTILINGUAL DATA:
 *    - Searches through multiLingualFieldsData for OneKey code labels
 *    - Uses dynamic key resolution via getOneKeyTableAttributes()
 *    - Falls back to English labels if translation not available
 * 
 * 4. SEARCH TYPE HANDLING:
 *    - VALUE SEARCH: Exact matching on field values
 *    - CHARACTER SEARCH: Fuzzy matching with multilingual support
 * 
 * @param data - Array of linked entity data to filter
 * @param filter - Filter configuration
 * @param searchText - Text to search for
 * @param suffix - Field suffix for formatted values
 * @param searchObjectKey - Key to extract from filtered results
 * @param convertFormattedString - Function to format strings for search
 * @param localizationService - Service for multilingual support
 * @returns Filtered array of linked entity data
 */
function filterLinkedEntityDataByType(
  data: any[],
  filter: FilterConfig,
  searchText: string,
  suffix: string,
  searchObjectKey: string,
  convertFormattedString: (text: string) => string,
  localizationService?: any,
): any[] {
  const field = filter.categoryPath;
  // Check both suffixed and non-suffixed field names for maximum compatibility
  const fields = [field + suffix, field];

  // VALUE SEARCH: Exact matching on field values
  if (filter.type === SuggestionPillType.CATEGORY_VALUESEARCH) {
    return data.filter((item) => {
      for (const field of fields) {
        const val = item[field];
        if (val === searchText) {
          return true;
        }
      }
      return false;
    });
  }

  // CHARACTER SEARCH: Fuzzy matching with complex multilingual support
  if (filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH) {
    const formattedSearchText = convertFormattedString(searchText).toLowerCase();

    return data.filter((item) => {
      for (const field of fields) {
        const val = item[field];
        const convertedVal = convertFormattedString(val).toLowerCase() || '';
        
        // COMPLEX MULTILINGUAL LOGIC: Check translated labels
        let formattedValueC = '';
        if (localizationService && val) {
          // Get translated string for mobile app form labels
          formattedValueC = localizationService.getTranslatedString(val, 'MobileAppForm') || '';
          formattedValueC = formattedValueC ? convertFormattedString(formattedValueC).toLowerCase() : '';
        }
        
        // COMPLEX ONEKEY LOGIC: Check OneKey multilingual data
        let formattedValueD = '';
        if (localizationService && !isEmpty(localizationService.multiLingualFieldsData)) {
          // Find OneKey record by code label ID
          const foundRecord = localizationService.multiLingualFieldsData.find((a: any) => a['omnione_onekeycodeslabelsid'] == val);
          const keyForOneKey = localizationService.getOneKeyTableAttributes();
          let translatedLang: string = '';
          
          if (foundRecord) {
            // Use dynamic key or fallback to English label
            translatedLang = foundRecord[keyForOneKey] ? foundRecord[keyForOneKey] || '' : foundRecord['omnione_en_long_label'] || '';
          }
          
          if (foundRecord && translatedLang) {
            formattedValueD = convertFormattedString(translatedLang).toLowerCase();
          }
        }
        
        // COMPLEX MATCHING: Check all possible value sources
        // 1. Direct field value
        // 2. Translated mobile app form label
        // 3. OneKey multilingual data
        if (convertedVal.includes(formattedSearchText) || 
            formattedValueC.includes(formattedSearchText) || 
            formattedValueD.includes(formattedSearchText)) {
          return true;
        }
      }
      return false;
    });
  }

  return [];
}

/**
 * Determines if a search configuration matches a filter
 * Handles different matching scenarios for assessment and regular filters
 * @param searchConfig - Search configuration to match against
 * @param filter - Filter configuration to match
 * @returns True if the configurations match
 */
function doesAccountSearchConfigMatch(
  searchConfig: any,
  filter: FilterConfig,
): boolean {
  // Handle assessment-based filters
  if (filter.configuredFrom === 'Assess') {
    return searchConfig.categoryName === filter.categoryName || searchConfig.categoryName === filter.categoryPath;
  } 
  // Handle filters with category path
  else if (filter.categoryPath) {
    return searchConfig.categoryRelativePath === filter.categoryPath;
  }
  // Default category name matching
  return searchConfig.categoryName === filter.categoryName;
}

function applyAccountSpecialConfigAdjustments(
  filter: FilterConfig,
  searchConfig: any,
): void {
  if (searchConfig) {
    filter.categoryPath = searchConfig.categoryRelativePath;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AdvancedAccountSearchService {
  constructor(
    private readonly translate: TranslateService,
    private readonly searchConfigService: SearchConfigService,
    private readonly searchConfigDataService: SearchConfigDataService,
    private readonly contactDataService: ContactDataService,
    private readonly customerAssessService: CustomerAssessService,
    private readonly authenticationOfflineService: AuthenticationService,
    private readonly disk: DiskService,
    private readonly device: DeviceService,
    private readonly accountService: AccountOfflineService,
    private readonly userTagService: UserTagService,
    private readonly localizationService: LocalizationService,
    private readonly accountDataService: AccountDataService,
    private readonly uiService: UIService,
    private readonly opportunityMgmtService: OpportunityManagementService,
  ) { }

  private matchLocalized(item: string, value: string): boolean;
  private matchLocalized<X, Y>(
    selector: (item: X) => Y,
    value: string,
  ): (item: X) => boolean;
  private matchLocalized<X, Y>(
    selector: ((item: X) => Y) | string,
    value: string,
  ): ((item: X) => boolean) | boolean {
    if (typeof selector === 'string') {
      return value === selector || this.translate.instant(value) === selector;
    }
    return (item: X) => {
      const itemValue = selector(item);
      return itemValue === value || this.translate.instant(value) === itemValue;
    };
  }

  private getAccounts() {
    return this.accountService.isOneKeyAffiliatedToAccount
      ? this.accountService.oneKeyAccounts
      : this.accountService.accounts;
  }

  // Account-specific helper methods - migrated from searchAccountsList in account-list.ts

  private getAccountObjectValues(accountObject: any): string {
    if (!accountObject) return '';

    const objString: string[] = [];

    for (const key in accountObject) {
      if (!ACCOUNT_SEARCHABLE_FIELDS.has(key) || !accountObject[key]) continue;

      const value = accountObject[key];

      if (Array.isArray(value)) {
        value.forEach(item => {
          const nestedValue = this.getAccountObjectValues(item);
          if (nestedValue) objString.push(nestedValue);
        });
      } else if (key === "extendedDetails") {
        extractExtendedDetails(value, objString);
      } else {
        objString.push(String(value));
      }
    }

    return this.searchConfigService.convertFormattedString(objString.join(',').toLowerCase());
  }


  private getAccountEntityLevelCharSearchPredicate(filters: FilterConfig[]): Predicate<any> | undefined {
    const entityLevelCharSearch = filters.find(o => o.type === SuggestionPillType.ENTITY_LEVEL_CHARACTERSEARCH);

    if (!entityLevelCharSearch?.charSearchText) return undefined;

    const formattedSearchText = this.searchConfigService.convertFormattedString(entityLevelCharSearch.charSearchText).toLowerCase();
    const formattedSearchTextSplit = formattedSearchText ? formattedSearchText.split(" ") : [];

    if (!formattedSearchTextSplit.length) return undefined;

    return (account: any) => {
      if (!account) return false;
      const accountEntityLevel = this.getAccountObjectValues(account);
      return formattedSearchTextSplit.every(searchText =>
        accountEntityLevel.includes(searchText)
      );
    };
  }

  private getAccountListPeriodTagPredicate(filters: FilterConfig[]): Predicate<any> | undefined {
    const listPeriodTagFilter = filters.find(f => f.categoryName === 'listPeriodTag');
    if (!listPeriodTagFilter?.selectedFacet) return undefined;

    return (account: any) =>
      account?.tagLabels?.includes(listPeriodTagFilter.selectedFacet);
  }

  private mapForAccountUserTag(selectedData: FilterConfig[]): FilterConfig[] {
    if (!selectedData?.length) return [];

    const copySelectedData = selectedData.filter(x => x.categoryName !== "Tag");
    const tagCategories = [...TAG_CATEGORIES, this.translate.instant("Tag")];
    const tagData = selectedData.filter(x => tagCategories.includes(x.categoryName));

    if (!tagData.length) return copySelectedData;

    const tagIds: any[] = [];
    tagData.forEach(c => {
      if (!c.selectedFacet) return;
      const foundTag = this.userTagService.accountTags?.find(v => v.indskr_name === c.selectedFacet)?.entityRecords;
      if (foundTag) tagIds.push(foundTag);
    });

    const accountIds = [...new Set(tagIds.flat().map(c => c?.id).filter(Boolean))];

    if (accountIds.length === 0) {
      return copySelectedData;
    }

    const tagFilters: FilterConfig[] = accountIds.map(accountId => ({
      selectedFacet: accountId,
      categoryPath: "accountid",
      categoryName: "Tag",
      categoryDisplayName: "Tag",
      isSubCategory: false,
    }));

    return [...copySelectedData, ...tagFilters];
  }

  private populateAccountConfiguredFrom(filters: FilterConfig[]): void {
    if (!filters?.length) return;

    filters.forEach(data => {
      if (!data?.configuredFrom && data?.categoryName) {
        const foundSearchIndexConfig = this.searchConfigService.accountConfiguredSearchIndexConfig?.find(
          c => c.categoryName === data.categoryName
        );
        if (foundSearchIndexConfig?.configuredFrom) {
          data.configuredFrom = foundSearchIndexConfig.configuredFrom;
        }
      }
    });
  }

  private groupAccountSuggestionsByCategory(selectedSuggestionsDataCopy: FilterConfig[]): { category: string, suggestions: FilterConfig[] }[] {
    let groupedSelectedSuggestions: { category: string, suggestions: FilterConfig[] }[] = [];

    selectedSuggestionsDataCopy.forEach(data => {
      if (data && data.categoryName) {
        let groupIndex = groupedSelectedSuggestions.findIndex(group => group.category === data.categoryName);
        const suggestions = getSearchSuggestionsData(data);

        if (groupIndex > -1) {
          groupedSelectedSuggestions[groupIndex].suggestions.push(...suggestions);
        } else {
          if (data.categoryName !== 'listPeriodTag') {
            groupedSelectedSuggestions.push({
              category: data.categoryName,
              suggestions: [...suggestions]
            });
          }
        }
      }
    });

    return groupedSelectedSuggestions;
  }


  private groupAccountFiltersByCategory(
    filters: FilterConfig[],
  ): Record<string, FilterConfig[]> {
    return _(filters)
      .filter(
        (f) =>
          f.categoryName &&
          (!f.isSubCategory || f.configuredFrom === 'Assess') &&
          !EXCLUDED_CATEGORIES.includes(f.categoryName),
      )
      .groupBy('categoryName')
      .value();
  }

  /**
   * Processes grouped filters and creates predicates for complex assessment logic
   * 
   * This method handles the most complex part of the search logic:
   * 1. Converts filter groups to grouped suggestions format
   * 2. Analyzes assessment flags (isAssessed/isUnAssessed) from multiple sources
   * 3. Creates OR predicates within each category (filters in same category are OR'ed)
   * 4. Creates AND predicates between categories (different categories are AND'ed)
   * 
   * Complex Assessment Logic:
   * - Direct "Assessed"/"UnAssessed" categories set flags directly
   * - "Assessments" category with nested suggestions also sets flags
   * - Flags are used to determine which assessment predicate to apply
   * 
   * @param query - Lodash collection of accounts for context
   * @param filterGroups - Filters grouped by category name
   * @returns Array of predicates to be applied
   */
  private async getAccountGroupedPredicates(
    query: Collection<any>,
    filterGroups: Record<string, FilterConfig[]>,
  ): Promise<Predicate<any>[]> {
    let predicates: Predicate<any>[] = [];
    
    // Convert filterGroups to grouped suggestions format for complex assessment logic
    // This maintains compatibility with the original searchAccountsList logic
    const groupedSelectedSuggestions = this.groupAccountSuggestionsByCategory(
      Object.values(filterGroups).flat()
    );
    
    // COMPLEX ASSESSMENT LOGIC: Analyze assessment flags from multiple sources
    // This replicates the complex flag detection from the original searchAccountsList
    let isAssessed: boolean = false;
    let isUnAssessed: boolean = false;
    
    if (!isEmpty(groupedSelectedSuggestions)) {
      groupedSelectedSuggestions.forEach(config => {
        // Direct assessment categories set flags immediately
        if (config.category === "Assessed" || config.category === "UnAssessed") {
          isAssessed = isAssessed || config.category === "Assessed";
          isUnAssessed = isUnAssessed || config.category === "UnAssessed";
        }
        // "Assessments" category with nested suggestions also affects flags
        if ((config.category === "Assessments" || config.category === this.translate.instant("Assessments")) && config.suggestions && config.suggestions.length > 0) {
          // Update flags based on nested suggestions within Assessments category
          isAssessed = isAssessed || config.suggestions.some(s => s.selectedFacet === "Assessed" || s.selectedFacet === this.translate.instant("ASSESSED"));
          isUnAssessed = isUnAssessed || config.suggestions.some(s => s.selectedFacet === "UnAssessed" || s.selectedFacet === this.translate.instant("UNASSESSED"));
        }
      });
    }
    
    // Process each category and create predicates
    for (const [category, filters] of Object.entries(filterGroups)) {
      let orPredicates: Predicate<any>[] = [];
      
      // Special handling for Assessments category with complex logic
      if (this.matchLocalized(category, 'Assessments')) {
        orPredicates.push(this.getAssessmentsSubPredicate(filters));
      }
      
      // Handle all other categories with their respective control data types
      for (const filter of filters) {
        orPredicates.push(
          await this.getAccountControlDataTypePredicate(filter, query),
        );
      }
      
      // Remove any undefined predicates
      orPredicates = orPredicates.filter(Boolean);
      if (!orPredicates.length) {
        continue;
      }
      
      // Create OR predicate for this category (filters within same category are OR'ed)
      // This means if ANY filter in the category matches, the account passes
      predicates.push((account: any) =>
        orPredicates.some((predicate) => predicate(account)),
      );
    }
    
    // Return predicates that will be AND'ed together (different categories are AND'ed)
    return predicates;
  }

  /**
   * Creates predicate for assessment-based filtering
   * 
   * COMPLEX LOGIC EXPLANATION:
   * This method handles the nuanced assessment filtering logic:
   * 
   * 1. If ONLY "Assessed" is selected: Returns accounts that ARE assessed
   * 2. If ONLY "UnAssessed" is selected: Returns accounts that are NOT assessed
   * 3. If BOTH are selected: Returns undefined (no filtering - all accounts pass)
   * 4. If NEITHER is selected: Returns undefined (no filtering - all accounts pass)
   * 
   * The key insight is that when both assessed and unassessed are selected,
   * it effectively means "show all accounts" regardless of assessment status.
   * 
   * @param filters - Array of assessment-related filters
   * @returns Predicate function or undefined if no filtering needed
   */
  private getAssessmentsSubPredicate(
    filters: FilterConfig[],
  ): Predicate<Account> {
    // Check if "Assessed" filter is present (with localization support)
    const hasAssess = filters.some(
      this.matchLocalized((x) => x.selectedFacet, 'Assessed'),
    );
    
    // Check if "UnAssessed" filter is present (with localization support)
    const hasUnassess = filters.some(
      this.matchLocalized((x) => x.selectedFacet, 'UnAssessed'),
    );

    // COMPLEX LOGIC: Only apply filtering if exactly one assessment type is selected
    // If both or neither are selected, no filtering is needed (show all accounts)
    if (!hasAssess || !hasUnassess) {
      // Get unique list of assessed account IDs
      const accountIds = _(this.customerAssessService.assessedAccounts)
        .map((c) => c.accountid)
        .uniq()
        .value();

      // Return predicate that checks if account is assessed based on the selected filter
      // hasAssess=true means "show only assessed accounts"
      // hasAssess=false means "show only unassessed accounts"
      return (account: Account) =>
        accountIds.includes(account.id) === hasAssess;
    }
    
    // If both assessed and unassessed are selected, return undefined (no filtering)
    // This allows all accounts to pass through regardless of assessment status
  }

  /**
   * Routes filter processing based on control data type
   * 
   * COMPLEX ROUTING LOGIC:
   * This method acts as a dispatcher that routes different types of filters
   * to their appropriate processing methods based on the controlDataType:
   * 
   * 1. 'DefaultConfig' - Handles special cases like hasTeamusers with complex API calls
   * 2. 'LinkedEntity' - Processes relationships between accounts and other entities
   * 3. 'Assess' - Handles assessment-related filtering with complex choice/flag logic
   * 4. Default - Handles raw field filtering (picklist, lookup, text fields, etc.)
   * 
   * The routing is essential because each data type requires completely different
   * processing logic, data sources, and filtering approaches.
   * 
   * @param filter - Filter configuration to process
   * @param query - Lodash collection of accounts for context
   * @returns Appropriate predicate based on control data type
   */
  private async getAccountControlDataTypePredicate(
    filter: FilterConfig,
    query: Collection<any>,
  ): Promise<Predicate<any>> {
    // Find matching search configuration for this filter
    const searchConfig =
      this.searchConfigService.accountConfiguredSearchIndexConfig?.find((c) =>
        doesAccountSearchConfigMatch(c, filter),
      );

    if (!searchConfig) return;

    // Apply special configuration adjustments (e.g., set categoryPath)
    applyAccountSpecialConfigAdjustments(filter, searchConfig);

    // COMPLEX ROUTING: Route to appropriate processing method based on control data type
    switch (searchConfig.controlDataType) {
      case 'DefaultConfig':
        // Handles special configurations like hasTeamusers with complex API workflows
        return await this.getAccountDefaultConfigPredicate(filter, query);

      case 'LinkedEntity':
        // Handles relationships between accounts and other entities (contacts, opportunities, etc.)
        return await this.getAccountLinkedEntityPredicate(filter, searchConfig, query);

      case 'Assess':
        // Handles assessment-related filtering with complex choice/flag attribute logic
        return await this.getAccountAssessPredicate(filter, query, searchConfig);

      default:
        // Handles raw field filtering (picklist, lookup, text, boolean fields, etc.)
        return this.getAccountRawFieldPredicate(filter, searchConfig);
    }
  }



  private async getAccountDefaultConfigPredicate(
    filter: FilterConfig,
    query: Collection<any>,
  ): Promise<Predicate<any>> {
    // Handle team users special case with complex logic from original
    if (filter.categoryPath === 'hasTeamusers' && filter.selectedFacet && filter.value) {
      return await this.getAccountTeamUsersPredicate(filter);
    }

    // Default Fuse search for this category
    return this.getAccountFusePredicate(query, filter);
  }

  /**
   * Creates predicate for linked entity filtering
   * 
   * COMPLEX LINKED ENTITY LOGIC:
   * This method handles the most complex filtering scenario - linked entities.
   * It processes relationships between accounts and other entities like contacts,
   * opportunities, and other accounts.
   * 
   * SPECIAL CASE - Account Affiliations:
   * Account-to-account affiliations are bidirectional and require special handling:
   * - 'From' relationships: Account A is affiliated FROM Account B
   * - 'To' relationships: Account A is affiliated TO Account B
   * - Both directions must be processed to find all related accounts
   * 
   * PROCESSING FLOW:
   * 1. Determine the database key based on entity type
   * 2. For account affiliations: Process both FROM and TO relationships
   * 3. For other entities: Process single relationship direction
   * 4. Filter linked entity data based on search criteria
   * 5. Extract account IDs from filtered results
   * 6. Return predicate that checks if account ID is in the filtered list
   * 
   * @param filter - Filter configuration
   * @param searchConfig - Search configuration with entity information
   * @param query - Lodash collection of accounts for context
   * @returns Predicate that filters accounts based on linked entity relationships
   */
  private async getAccountLinkedEntityPredicate(
    filter: FilterConfig,
    searchConfig: any,
    query: Collection<any>,
  ): Promise<Predicate<any>> {
    // Determine database key based on entity type
    const dbKey = searchConfig.parentEntity !== ACCOUNT_ACCOUNT_AFFILIATIONS_REF_ENTITY
      ? searchConfig.parentEntity
      : searchConfig.relationshipName;

    let accountIds: string[] = [];

    // COMPLEX LOGIC: Special handling for account-to-account affiliations
    if (searchConfig.parentEntity === ACCOUNT_ACCOUNT_AFFILIATIONS_REF_ENTITY) {
      // Account affiliations are bidirectional - we need to check both directions
      const dbKeyFrom = 'indskr_account_accountaccountaffiliation_affiliatedfromaccountid';
      const dbKeyTo = 'indskr_account_accountaccountaffiliation_affiliatedtoaccountid';

      // Process both FROM and TO relationships in parallel for performance
      const [rawLEDataFrom, rawLEDataTo] = await Promise.all([
        this.disk.retrieve(DB_KEY_PREFIXES.ACCOUNT_LINKED_ENTITY + dbKeyFrom),
        this.disk.retrieve(DB_KEY_PREFIXES.ACCOUNT_LINKED_ENTITY + dbKeyTo)
      ]);

      // Process FROM relationships (Account A is affiliated FROM Account B)
      if (rawLEDataFrom?.raw) {
        const fromIds = this.filterLinkedEntityData(rawLEDataFrom.raw, filter, searchConfig, 'indskr_accountaccountaffiliation.indskr_affiliatedfromaccountid');
        accountIds.push(...fromIds);
      }

      // Process TO relationships (Account A is affiliated TO Account B)
      if (rawLEDataTo?.raw) {
        const toIds = this.filterLinkedEntityData(rawLEDataTo.raw, filter, searchConfig, 'indskr_accountaccountaffiliation.indskr_affiliatedtoaccountid');
        accountIds.push(...toIds);
      }
    } else {
      // Standard linked entity processing (contacts, opportunities, etc.)
      const rawLEData = await this.disk.retrieve(DB_KEY_PREFIXES.ACCOUNT_LINKED_ENTITY + dbKey);
      if (rawLEData?.raw) {
        const ids = this.filterLinkedEntityData(rawLEData.raw, filter, searchConfig, 'accountid');
        accountIds.push(...ids);
      }
    }

    // Remove duplicates and return predicate
    const uniqueAccountIds = [...new Set(accountIds)];
    return (account: any) => uniqueAccountIds.includes(account.id);
  }

  /**
   * Creates predicate for assessment-based filtering
   * 
   * COMPLEX ASSESSMENT LOGIC:
   * This method handles assessment filtering with two distinct attribute types:
   * 
   * 1. CHOICE ATTRIBUTES (indskr_choicelabel):
   *    - Can have multiple comma-separated values
   *    - Requires exact matching for value search
   *    - Uses fuzzy search for character search
   *    - Example: "High,Medium" for priority assessment
   * 
   * 2. FLAG ATTRIBUTES (boolean):
   *    - Simple true/false values
   *    - Uses fuzzy search for both value and character search
   *    - Example: "Yes" or "No" for compliance assessment
   * 
   * PROCESSING FLOW:
   * 1. Determine if it's a choice or flag attribute
   * 2. Get assessment responses from the service
   * 3. Filter to only accounts in the current query context
   * 4. Get latest assessment per customer (most recent wins)
   * 5. Apply appropriate matching logic based on attribute type
   * 6. Return predicate that checks if account has matching assessment
   * 
   * @param filter - Filter configuration
   * @param query - Lodash collection of accounts for context
   * @param searchConfig - Search configuration with assessment details
   * @returns Predicate that filters accounts based on assessment responses
   */
  private async getAccountAssessPredicate(
    filter: FilterConfig,
    query: Collection<any>,
    searchConfig: any,
  ): Promise<Predicate<any>> {
    // Determine if this is a choice attribute (multiple values) or flag attribute (boolean)
    const isChoice = searchConfig.categoryRelativePath === 'indskr_choicelabel';
    const searchType = isChoice
      ? AssessmentAttributeType.CHOICE
      : AssessmentAttributeType.FLAG;

    // Use English category name if available, fallback to localized name
    const categoryName =
      searchConfig.categoryNameInEng || searchConfig.categoryName;

    // COMPLEX SEARCH TEXT LOGIC:
    // For choice attributes with English facet, use English value
    // Otherwise use character search text or selected facet
    const searchText =
      isChoice && filter.selectedFacetInEng
        ? filter.selectedFacetInEng
        : filter.charSearchText || filter.selectedFacet;

    // Get assessment responses from the service
    const assessments =
      await this.customerAssessService.getAssessmentResponsesForOnlineSearch(
        EntityOptions.ACCOUNT,
        searchType,
        categoryName,
      );

    // COMPLEX FILTERING: Get latest assessment per customer
    // This ensures we only consider the most recent assessment response
    const latestByCustomer = _(assessments)
      .filter((a) => query.some((acc) => acc.id === a['ca.indskr_customer']))
      .uniqBy('ca.indskr_customer')
      .orderBy(['modifiedon', 'createdon'], ['desc', 'desc'])
      .value();

    // COMPLEX CHOICE ATTRIBUTE LOGIC:
    // For choice attributes with value search, use exact matching on comma-separated values
    if (isChoice && filter.type === SuggestionPillType.CATEGORY_VALUESEARCH) {
      const matched = latestByCustomer.filter((a) =>
        a.indskr_choicelabel?.split(',')?.includes(searchText),
      );

      return account => matched.some((a) => a['ca.indskr_customer'] === account.id);
    }

    // For all other cases (flag attributes or character search), use fuzzy search
    const accountIds = fuseSearch(
      latestByCustomer,
      filter,
      0.2, // Lower threshold for assessment search
      ['indskr_choicelabel'],
      searchText,
    )
      .map('ca.indskr_customer')
      .uniq()
      .value();

    return (account: any) => accountIds.includes(account.id);
  }

  private getAccountRawFieldPredicate(
    filter: FilterConfig,
    searchConfig: any,
  ): Predicate<any> {
    let searchKey = getAccountRawFieldKey(filter, searchConfig);
    let searchText = getAccountRawSearchText(filter, searchConfig);

    switch (filter.type) {
      case SuggestionPillType.CATEGORY_VALUESEARCH:
      case SuggestionPillType.BOOLEAN_FIELD:
        return (account: any) => account.raw[searchKey] === searchText;

      case SuggestionPillType.CATEGORY_CHARACTERSEARCH:
        return (account: any) => {
          const val = account.raw[searchKey];
          const accountVal = val
            ? this.searchConfigService.convertFormattedString(val).toLowerCase()
            : '';
          const target = this.searchConfigService
            .convertFormattedString(searchText)
            .toLowerCase();
          return accountVal.includes(target);
        };

      case SuggestionPillType.CUSTOM_CATEGORY_CHARACTERSEARCH:
        searchKey = 'accountName';
        return (account: any) => {
          const val = account.raw[searchKey];
          const accountVal = val
            ? this.searchConfigService.convertFormattedString(val).toLowerCase()
            : '';
          const target = this.searchConfigService
            .convertFormattedString(filter.charSearchText)
            .toLowerCase();
          return (
            account.raw.hasOwnProperty('accountid') &&
            accountVal.includes(target)
          );
        };

      case SuggestionPillType.CUSTOM_CATEGORY_VALUESEARCH:
        searchKey = 'accountid';
        return (account: any) => account.raw[searchKey] === filter.id;

      default:
        return; // no-op if unknown type
    }
  }



  private getAccountFusePredicate(
    query: Collection<any>,
    filter: FilterConfig,
  ): Predicate<any> {
    const threshold = filter.categoryPath === 'allPhoneNumbers'
      ? PHONE_SEARCH_THRESHOLD
      : FUSE_SEARCH_THRESHOLD;

    const results = fuseSearch(query.value(), filter, threshold);

    return (account: any) => account?.id && results.some((r) => r.id === account.id);
  }

  private getAccountOpportunityPredicate(filters: FilterConfig[]): Predicate<any> | undefined {
    const opportunityFilters = filters.filter(f => f.categoryName === 'Opportunities');
    if (!opportunityFilters.length) return undefined;

    const accountIds: string[] = [];

    opportunityFilters.forEach(config => {
      let searchText: string = config.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH
        ? config.charSearchText
        : config.selectedFacet;
      const fuseOptions = {
        keys: [config.categoryPath],
        threshold: 0.1,
        distance: 1000
      };

      let newFuse = new Fuse(this.opportunityMgmtService.opportunities, fuseOptions);
      let results: any = newFuse.search(searchText);
      results.forEach(opportunity => {
        if (opportunity.item?.accountID) {
          // Use account lookup like in original logic
          const thisAccount = this.accountService.getAccountById(opportunity.item.accountID);
          if (!isEmpty(thisAccount)) {
            accountIds.push(thisAccount.id);
          }
        }
      });
    });

    const uniqueAccountIds = [...new Set(accountIds)];
    return (account: any) => uniqueAccountIds.includes(account.id);
  }

  private getAccountTagSuggestionPredicate(
    filters: FilterConfig[],
  ): Predicate<any> | undefined {
    const tagCategories = [...TAG_CATEGORIES, this.translate.instant('TAG')];
    const tagFilters = filters.filter((f) => tagCategories.includes(f.categoryName));

    if (!tagFilters.length) return undefined;

    // Use account lookup like in original logic
    const accountIds: string[] = [];
    tagFilters.forEach(filter => {
      if (filter.selectedFacet) {
        const thisAccount = this.accountService.getAccountById(filter.selectedFacet);
        if (!isEmpty(thisAccount)) {
          accountIds.push(thisAccount.id);
        }
      }
    });

    return (account: any) => accountIds.includes(account.id);
  }

  private filterLinkedEntityData(
    linkedEntityData: any[],
    filter: FilterConfig,
    searchConfig: any,
    searchObjectKey: string,
  ): string[] {
    const suffix = getLinkedEntitySuffix(searchConfig, filter);
    const searchText = getLinkedEntitySearchText(filter, searchConfig);
    const filteredLinkedData = filterLinkedEntityDataByType(
      linkedEntityData,
      filter,
      searchText,
      suffix,
      searchObjectKey,
      this.searchConfigService.convertFormattedString.bind(this.searchConfigService),
      this.localizationService,
    );

    return _(filteredLinkedData)
      .map((entry) => entry[searchObjectKey])
      .uniq()
      .value();
  }




  private async getAccountTeamUsersPredicate(filter: FilterConfig): Promise<Predicate<any>> {
    try {
      await this.uiService.displayLoader();

      const hasCLM = this.authenticationOfflineService.hasFeatureAction(
        FeatureActionsMap.CUSTOMER_LIST_MANAGEMENT,
      );

      let targetResponse: AccountGlobalSearchDTO[] = [];
      if (hasCLM) {
        try {
          targetResponse = await this.accountDataService.getTargetAccountsBasedOnPositionId(filter.value);
        } catch (error) {
          console.error('TargetAccounts - AccountList: ', error);
        }
      }
      if (!isEmpty(targetResponse)) {
        const fallbackResponse = await this.accountDataService.getAccountsBasedOnPositionId(filter.value);
        if (!isEmpty(fallbackResponse)) {
          targetResponse = fallbackResponse.map(id => ({ accountid: id } as AccountGlobalSearchDTO));
        }
      }

      if (!isEmpty(targetResponse)) {
        this.accountService.mapGlobalSearchedAccounts(targetResponse, true);
        return (account: any) => {
          const globalResults = this.accountService.globalSearchResults;
          return globalResults.some((result: any) => result.id === account.id);
        };
      }
    } catch (error) {
      console.log(error);
    } finally {
      await this.uiService.dismissLoader();
    }

    return () => false; // No matches if all fails
  }

  /**
   * Main method to get accounts with applied filters
   * 
   * This method orchestrates the entire search process:
   * 1. Maps user tags to account IDs
   * 2. Applies entity-level character search (AND logic for multiple terms)
   * 3. Applies list period tag filtering
   * 4. Groups and applies category-based filters
   * 5. Applies opportunity-based filtering
   * 6. Applies tag-based filtering
   * 7. Returns Lodash chain with filtered accounts
   * 
   * @param filters - Array of filter configurations
   * @returns Filtered accounts array
   */
  public async getAccountsWithFilters(
    filters: FilterConfig[],
  ) {
    filters ??= [];

    // Map user tags for accounts - converts tag names to account IDs
    filters = this.mapForAccountUserTag(filters);

    // Get initial accounts (OneKey vs regular accounts)
    let filteredAccounts = this.getAccounts();
    let query = _(filteredAccounts);
    let predicates: Predicate<any>[] = [];

    // Apply entity level character search with AND logic for multiple terms
    predicates.push(this.getAccountEntityLevelCharSearchPredicate(filters));

    // Apply list period tag filter (special case for account tags)
    predicates.push(this.getAccountListPeriodTagPredicate(filters));

    // Populate configured from values for saved searches
    this.populateAccountConfiguredFrom(filters);

    // Group filters by category for efficient processing
    const filtersGroupedByCategory = this.groupAccountFiltersByCategory(filters);

    // Apply filters grouped by category (handles complex assessment logic)
    predicates.push(
      ...(await this.getAccountGroupedPredicates(
        query,
        filtersGroupedByCategory,
      )),
    );

    // Apply opportunity-based filtering
    predicates.push(this.getAccountOpportunityPredicate(filters));

    // Apply tag suggestion filtering
    predicates.push(this.getAccountTagSuggestionPredicate(filters));

    // Remove any undefined predicates
    predicates = predicates.filter(Boolean);

    // Apply all predicates using functional reduce approach
    if (predicates.length > 0) {
      query = predicates.reduce(
        (acc, predicate) => acc.filter((a) => predicate(a)),
        query,
      );
    }

    // Return final filtered results
    return query;
  }

  public async initAccountSearchConfig() {
    const accounts = this.getAccounts();
    this.searchConfigService.setCustomCategorySearchIndexConfig(
      CustomCategorySearchToolName.ACCOUNTS,
      accounts,
    );

    // Initialize account-specific search configurations
    this.mapAccountTagsToSearchConfig();
    this.mapOpportunitiesToSearchIndex();
    await this.mapChildUsersWithPositionToAccountsSearchConfig();

    // Handle config updates
    if (this.searchConfigService.configUpdateRequired) {
      this.searchConfigService.updateSearchConfigsForSelectedLanguage();
      this.searchConfigService.configUpdateRequired = false;
    } else {
      this.searchConfigService.configUpdateRequired = true;
    }

    let filters = this.searchConfigService.getDefaultFilters('Accounts');
    return filters;
  }

  private mapAccountTagsToSearchConfig(): void {
    // Map account tags to search config similar to how contact tags are mapped
    const tagConfig = this.searchConfigService.accountConfiguredSearchIndexConfig?.find(
      x => x.categoryName === "Tag" || x.categoryName === this.translate.instant("Tag")
    );

    if (tagConfig) {
      const values = this.userTagService.accountTags.map(x => x.indskr_name);
      tagConfig.values = values;
    }
  }

  private mapOpportunitiesToSearchIndex(): void {
    const opportunities = this.opportunityMgmtService.opportunities;
    if (!opportunities?.length) return;

    const opportunitiesConfig = this.searchConfigService.accountConfiguredSearchIndexConfig?.find(
      config => config.categoryName === 'Opportunities'
    );

    if (!opportunitiesConfig?.values) return;

    const existingValues = new Set(opportunitiesConfig.values);

    opportunities
      .filter(opportunity => opportunity.accountID && opportunity.opportunityName)
      .forEach(opportunity => {
        if (!existingValues.has(opportunity.opportunityName)) {
          opportunitiesConfig.values.push(opportunity.opportunityName);
          existingValues.add(opportunity.opportunityName);
        }
      });
  }

  private async mapChildUsersWithPositionToAccountsSearchConfig(): Promise<void> {
    if (this.device.isOffline) return;

    try {
      // Load child users if not already loaded
      if (isEmpty(this.searchConfigDataService.childUsersWithPositions)) {
        this.searchConfigDataService.childUsersWithPositions = await this.contactDataService
          .getAllChildUsersBasedOnPositionId(this.authenticationOfflineService.user.positionID);
      }

      const childUsers = this.searchConfigDataService.childUsersWithPositions;
      if (isEmpty(childUsers)) return;

      // Find team users config once
      const teamUsersConfig = this.searchConfigService.accountConfiguredSearchIndexConfig?.find(
        config => config.categoryName === 'Teamusers'
      );

      if (!teamUsersConfig?.mappingValues) return;

      // Create set for efficient lookup
      const existingMappingValues = new Set(
        teamUsersConfig.mappingValues.map(m => m.formattedValue)
      );

      // Process child users
      childUsers
        .filter(childUser => childUser.userfullname)
        .forEach(childUser => {
          if (!existingMappingValues.has(childUser.userfullname)) {
            teamUsersConfig.mappingValues.push({
              actualValue: childUser.childpositionid.toString(),
              formattedValue: childUser.userfullname.toString()
            });
            existingMappingValues.add(childUser.userfullname);
          }
        });

      // Transform child users data
      this.searchConfigDataService.childUsersWithPositions = childUsers.map(childUser => {
        const picked = pick(childUser, ['childpositionid', 'userfullname']);
        return {
          ...picked,
          id: `filter_menu_Teamusers_${picked.userfullname}`,
          label: picked.userfullname,
          value: picked.childpositionid.toString(),
          isChecked: false,
          category: 'Teamusers',
          isExpanded: false,
          wasCalled: false,
          childUsers: [],
          depth: 0
        };
      });
    } catch (error) {
      console.error('Error mapping child users with position to accounts search config:', error);
    }
  }

}
